import React, { useEffect, useRef } from 'react';

interface CanvasScrollSequenceProps {
  /** URL prefix for frames, e.g. '/frames/interior/'. Files must be 001.jpg, 002.jpg, ... */
  framePrefix: string;
  frameCount: number;
  ext?: string;
  /** 0..1 scroll progress. Defaults to the canvas's own position in the viewport. */
  getProgress?: () => number;
  /** Used by the default progress: fraction of the element height over which it scrubs. */
  scrub?: number;
  style?: React.CSSProperties;
}

/**
 * Scroll-scrubbed image sequence drawn on a <canvas>. Works on every device
 * (including iOS, where seeking a <video> paints nothing) because it only draws
 * preloaded images. This is the Apple-style approach to scroll video on mobile.
 */
const CanvasScrollSequence: React.FC<CanvasScrollSequenceProps> = ({
  framePrefix,
  frameCount,
  ext = 'jpg',
  getProgress,
  scrub = 1,
  style,
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const rafRef = useRef<number>(0);
  const imagesRef = useRef<HTMLImageElement[]>([]);
  const lastIdxRef = useRef<number>(-1);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    const dpr = Math.min(window.devicePixelRatio || 1, 2);

    // Preload all frames.
    const images: HTMLImageElement[] = [];
    for (let i = 1; i <= frameCount; i++) {
      const img = new Image();
      img.src = `${framePrefix}${String(i).padStart(3, '0')}.${ext}`;
      images.push(img);
    }
    imagesRef.current = images;

    const sizeCanvas = () => {
      const w = canvas.clientWidth;
      const h = canvas.clientHeight;
      canvas.width = Math.round(w * dpr);
      canvas.height = Math.round(h * dpr);
    };

    // Draw a frame "cover"-fit (fill the canvas, crop overflow, centered).
    const drawIndex = (idx: number) => {
      const img = images[idx];
      if (!img || !img.complete || !img.naturalWidth) return;
      const cw = canvas.width;
      const ch = canvas.height;
      const scale = Math.max(cw / img.naturalWidth, ch / img.naturalHeight);
      const dw = img.naturalWidth * scale;
      const dh = img.naturalHeight * scale;
      ctx.clearRect(0, 0, cw, ch);
      ctx.drawImage(img, (cw - dw) / 2, (ch - dh) / 2, dw, dh);
      lastIdxRef.current = idx;
    };

    const progressFn =
      getProgress ||
      (() => {
        const rect = canvas.getBoundingClientRect();
        const span = rect.height * Math.max(0.2, scrub);
        return Math.min(1, Math.max(0, -rect.top / span));
      });

    const render = () => {
      const p = progressFn();
      const idx = Math.min(frameCount - 1, Math.max(0, Math.round(p * (frameCount - 1))));
      if (idx !== lastIdxRef.current) drawIndex(idx);
    };

    const onScroll = () => {
      if (rafRef.current) return;
      rafRef.current = requestAnimationFrame(() => {
        rafRef.current = 0;
        render();
      });
    };

    // First frame paints as soon as it (and the canvas size) is ready.
    images[0].onload = () => {
      sizeCanvas();
      lastIdxRef.current = -1;
      render();
    };
    if (images[0].complete) {
      sizeCanvas();
      render();
    }

    const onResize = () => {
      sizeCanvas();
      lastIdxRef.current = -1;
      render();
    };

    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onResize);

    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onResize);
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = 0;
      }
      imagesRef.current = [];
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [framePrefix, frameCount, ext, scrub]);

  return <canvas ref={canvasRef} aria-hidden style={{ display: 'block', width: '100%', height: '100%', ...style }} />;
};

export default CanvasScrollSequence;
